import { useReport } from "@/api/report-query";
import { useStation } from "@/api/station-query";
import { RouteLocationRaw } from "vue-router";
import { useDist } from "../../api/distributor-query";
import { useExternalRecipient } from "../../api/external-recipient-query";
import { useSite } from "../../api/site-query";
import { useUser } from "../../api/user-query";

export const LinkKeys = [
	"distributor",
	"site",
	"sites",
	"stations",
	"reports",
	"internalRecipients",
	"externalRecipients",
] as const;
export type LinkKeys = (typeof LinkKeys)[number];

export function isLinkChange(name: string) {
	return LinkKeys.includes(name as any);
}

export type LinkOptions = {
	name: string;
	value: string | null | undefined;
};

export function useAuditLinkResolver(opts: MaybeRefOrGetter<LinkOptions>) {
	function valueIfMatchingName(requiredName: LinkKeys): string {
		const { name, value } = toValue(opts);
		if (value == null) {
			return "";
		}

		if (name === requiredName) {
			return value;
		}

		return "";
	}

	const distId = computed(() => valueIfMatchingName("distributor"));
	const siteId = computed(() => valueIfMatchingName("sites") || valueIfMatchingName("site"));
	const stationId = computed(() => valueIfMatchingName("stations"));
	const reportId = computed(() => valueIfMatchingName("reports"));
	const userId = computed(() => valueIfMatchingName("internalRecipients"));
	const externalRecipientId = computed(() => valueIfMatchingName("externalRecipients"));

	const { site } = useSite(siteId);
	const { user } = useUser(userId);
	const { station } = useStation(stationId);
	const { report } = useReport(reportId);
	const { externalRecipient } = useExternalRecipient(externalRecipientId);
	const { distributor } = useDist(distId);

	return computed(() => {
		if (site.value != null) {
			return {
				location: {
					name: "sites-id-settings",
					params: { id: site.value.id },
				},
				text: site.value.name,
			};
		}

		if (station.value != null) {
			return {
				location: {
					name: "stations-id-index",
					params: { id: station.value.id },
				},
				text: station.value.name,
			};
		}

		if (report.value != null) {
			return {
				location: {
					name: "reports-id-settings",
					params: { id: report.value.id },
				},
				text: report.value.title,
			};
		}

		if (user.value != null) {
			let location: RouteLocationRaw;
			if (user.value.scope.type === "distributor") {
				location = {
					name: "distributor-id-users-userid",
					params: { id: user.value.scope.id, userid: user.value.id },
				};
			} else if (user.value.scope.type === "space") {
				location = {
					name: "spaces-id-users-userid",
					params: {
						id: user.value.scope.id,
						userid: user.value.id,
					},
				};
			} else {
				return null;
			}

			return {
				location,
				text: user.value.name,
			};
		}

		if (externalRecipient.value != null) {
			const { email, phoneNumber } = externalRecipient.value;
			if (email) {
				return {
					text: email,
				};
			}
			if (phoneNumber) {
				return {
					text: phoneNumber,
				};
			}
		}

		if (distributor.value != null) {
			return {
				location: {
					name: "distributor-id-settings",
					params: { id: distributor.value.id },
				},
				text: distributor.value.name,
			};
		}

		return null;
	});
}
